import { ManageAccounts, Person } from '@mui/icons-material';
import { Box, Typography, Button, Tooltip, Checkbox, FormControlLabel, Alert, Divider } from '@mui/material';
import { t } from 'i18next';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { useCallback, useEffect, useState } from 'react';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { deleteFamily, getFamily } from 'src/shared/services/familyService';
import { toast } from 'react-toastify';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Schema } from 'yup';
import * as yup from 'yup';
import dayjs from 'dayjs';
import { FamilyMemberNew } from '../models/familyMember';
import { FormMemberFamilyPut, MemberFamilyPutRequest } from '../models/formMemberFamilyPut';
import { MemberFamilyDeleteSecedePickers } from './memberFamilyDeleteSecedePickers';

type MemberFamilyDeleteFamilyModalProps = {
  modalOpen: boolean;
  onClose: () => void;
  memberId: number;
  onDeleteSuccess: () => void;
};

export const MemberFamilyDeleteFamilyModal = (props: MemberFamilyDeleteFamilyModalProps) => {
  const { modalOpen, onClose, memberId, onDeleteSuccess } = props;
  const isMounted = useIsMounted();

  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState<FamilyMemberNew[]>([]);
  const [checked, setChecked] = useState(false);

  const MemberFamilyPutSchema: Schema<FormMemberFamilyPut> = yup.object().shape({
    idsToSecede: yup.array().required(),
    resCause: yup.string().required(`${t('member:family.required')}`),
    resDate: yup.string().required(`${t('member:family.required')}`),
  });
  const formMethods = useForm<FormMemberFamilyPut>({
    resolver: yupResolver(MemberFamilyPutSchema),
    defaultValues: {
      idsToSecede: [],
      resCause: '',
      resDate: '',
    },
  });

  const { handleSubmit, setValue, getValues, reset } = formMethods;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getFamily(0, 100, 'member_id', 'asc', memberId);
      ConsoleHelper.log(res);
      if (isMounted()) {
        setMembers(res.data.results);
      }
    } catch (error) {
      if (isMounted()) {
        ConsoleHelper.log(error);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [isMounted, memberId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onCloseHandler = () => {
    reset();
    setChecked(false);
    onClose();
  };
  const onDeleteFamilyHandler = async (req: MemberFamilyPutRequest) => {
    ConsoleHelper.log('onDeleteFamily::', req);

    setLoading(true);
    try {
      await deleteFamily(memberId, req);
      if (isMounted()) {
        toast.success(t('member:family.deleteWholeFamilySuccess'));
        reset();
        setChecked(false);
        onDeleteSuccess();
      }
    } catch (error) {
      if (isMounted()) {
        ConsoleHelper.log(error);
        toast.error(t('member:family.deleteWholeFamilyError'));
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  };

  const onSubmitWithSecede = (data: FormMemberFamilyPut) => {
    const dataToPut: MemberFamilyPutRequest = {
      ...data,
      resDate: dayjs(data.resDate).format('YYYYMMDD'),
    };
    onDeleteFamilyHandler(dataToPut);
  };

  const checkboxHandler = (event: React.ChangeEvent<HTMLInputElement>, id: number) => {
    const prevIds = getValues('idsToSecede') as number[]; // Assuming idsToSecede is an array of numbers

    if (!prevIds.includes(id)) {
      setChecked(true);
      setValue('idsToSecede', [...prevIds, id]);
    } else {
      // Remove id from the array
      setValue(
        'idsToSecede',
        prevIds.filter((existingId) => existingId !== id),
      );
    }
    // Check if the updated array is empty
    const updatedIds = getValues('idsToSecede') as number[];
    if (updatedIds.length < 1) {
      setChecked(false);
      reset();
    }
  };

  // ConsoleHelper.log(watch('idsToSecede'));
  // ConsoleHelper.log('locale::', idsToSecede);
  // ConsoleHelper.log(checked);

  return (
    <GenericDialog
      open={modalOpen}
      onClose={onCloseHandler}
      onSave={() => ConsoleHelper.log('save')}
      maxWidth='xs'
      showDialogActions={false}
      padding={0}
      dialogTitle={t('member:family.deleteWholeFamily')}
      dialogContent={
        <>
          <Alert severity='info'>
            <Typography>{`${t('member:family.deleteMemberSameTime')}`}</Typography>
          </Alert>
          <Divider />
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmitWithSecede)}>
              <Box display='flex' flexDirection='column' p={2}>
                <Box display='flex' flexDirection='row' justifyContent='flex-end'>
                  <Typography fontWeight={500}>{t('Utträd')}</Typography>
                </Box>
                {members.map((member) => {
                  return (
                    <Box key={member.memberNr} display='flex' flexDirection='row' alignItems='center' justifyContent='space-between'>
                      <Box display='flex' flexDirection='row' alignItems='center'>
                        {member.mainMember ? (
                          <Tooltip title={t('member:memberDetails.headMember')} arrow>
                            <ManageAccounts fontSize='small' />
                          </Tooltip>
                        ) : (
                          <Tooltip title={t('member:memberDetails.familyMember')} arrow>
                            <Person fontSize='small' />
                          </Tooltip>
                        )}
                        <Typography ml={1}>{`${member.firstName} ${member.lastName}`}</Typography>
                      </Box>
                      <Box>
                        <FormControlLabel
                          label=''
                          labelPlacement='start'
                          control={
                            <Checkbox
                              name='idToSecede'
                              onChange={(event) => checkboxHandler(event, member.mainMember ? member.member_id : member.familymember_id)}
                            />
                          }
                        />
                      </Box>
                    </Box>
                  );
                })}
                {checked && <MemberFamilyDeleteSecedePickers checked={checked} />}
              </Box>
              <Box
                display='flex'
                flexDirection='row'
                justifyContent='flex-end'
                width='100%'
                p={1}
                sx={{ borderTop: `1px solid rgba(0, 0, 0, 0.12)` }}
              >
                <Button disabled={loading} variant='outlined' color='primary' disableElevation onClick={onClose} sx={{ mr: 1 }}>
                  {t('common:button.cancel')}
                </Button>
                {checked ? (
                  <Button disabled={loading} autoFocus variant='contained' color='primary' disableElevation type='submit'>
                    {t('common:button.yes')}
                  </Button>
                ) : (
                  <Button disabled={loading} autoFocus variant='contained' color='primary' disableElevation onClick={() => onDeleteFamilyHandler({})}>
                    {t('common:button.yes')}
                  </Button>
                )}
              </Box>
            </form>
          </FormProvider>
        </>
      }
    />
  );
};
